import React from "react";
import { LuChevronsUpDown } from "react-icons/lu";
import { type Merge } from "type-fest";

import { css, cx, sva } from "#styled-system/css";

export const TableStyle = sva({
  slots: [
    "body",
    "bodyRow",
    "foot",
    "footRow",
    "head",
    "headRow",
    "root",
    "virtualInnerBody",
    "virtualOuterBody",
    "virtualBodyRow",
  ],
  base: {
    body: {
      borderColor: "inherit",
      borderStyle: "inherit",
      flexGrow: 1,
      minWidth: "100%",
      overflowY: "auto",
    },
    bodyRow: {
      alignItems: "center",
      display: "grid",
      borderColor: "inherit",
      borderStyle: "inherit",
      columnGap: "16px",
      minHeight: "52px",
      minWidth: "100%",
      padding: "8px 16px",
      "&:has(+ *)": { borderBottomWidth: "1px" },
    },
    foot: {
      borderColor: "inherit",
      borderStyle: "inherit",
      borderTopWidth: "1px",
      minWidth: "100%",
    },
    footRow: {
      alignItems: "center",
      display: "grid",
      borderColor: "inherit",
      borderStyle: "inherit",
      columnGap: "16px",
      minWidth: "100%",
      padding: "16px",
      "&:has(+ *)": { borderBottomWidth: "1px" },
    },
    head: {
      borderColor: "inherit",
      borderStyle: "inherit",
      borderBottomWidth: "1px",
      minWidth: "100%",
    },
    headRow: {
      alignItems: "center",
      display: "grid",
      borderColor: "inherit",
      borderStyle: "inherit",
      columnGap: "16px",
      minWidth: "100%",
      padding: "16px",
      "&:has(+ *)": { borderBottomWidth: "1px" },
    },
    root: {
      backgroundColor: "white",
      borderColor: "border",
      borderRadius: "8px",
      borderStyle: "solid",
      borderWidth: "1px",
      display: "flex",
      flexDirection: "column",
      minWidth: "max-content",
    },
    virtualBodyRow: {
      alignItems: "center",
      display: "grid",
      borderColor: "inherit",
      borderStyle: "inherit",
      columnGap: "16px",
      minWidth: "100%",
      overflowY: "clip",
      paddingX: "16px",
      position: "absolute",
      "&:has(+ *)": { borderBottomWidth: "1px" },
    },
    virtualInnerBody: {
      borderColor: "inherit",
      borderStyle: "inherit",
      minWidth: "100%",
      position: "relative",
    },
    virtualOuterBody: {
      borderColor: "inherit",
      borderStyle: "inherit",
      flexGrow: 1,
      maxHeight: "max-content",
      minWidth: "100%",
      overflowY: "auto",
    },
  },
});

/** @deprecated 代わりに`TABLE_STYLE`または`TableStyle`を使用する。 */
export const tableStyles = TableStyle;

/**
 * **テーブルのスタイル**
 * @tutorial `grid-template-columns`で列の幅を定義する。
 * @tutorial `data-scope`属性と`data-part`属性を指定して、
 *           `<div />`要素がテーブルのどの部分に対応するかを定義する。
 * @tutorial `max-height: max-content`を指定すると高さの余白が無くなる。
 *
 * @example
 * ```tsx
 * <div className={css(TABLE_STYLE, { gridTemplateColumns: "auto" })}>
 *   <div data-part="head" data-scope="table">
 *     <div data-part="head-row" data-scope="table">
 *       <div data-part="head-cell" data-scope="table" />
 *     </div>
 *   </div>
 *
 *   <div data-part="body" data-scope="table">
 *     <div data-part="body-row" data-scope="table">
 *       <div data-part="body-cell" data-scope="table" />
 *     </div>
 *   </div>
 *
 *   <div data-part="foot" data-scope="table">
 *     <div data-part="foot-row" data-scope="table">
 *       <div data-part="foot-cell" data-scope="table" />
 *     </div>
 *   </div>
 * </div>
 * ```
 */
export const TABLE_STYLE = css.raw({
  backgroundColor: "white",
  borderColor: "#E4E4E7",
  borderRadius: "6px",
  borderStyle: "solid",
  borderWidth: "1px",
  display: "grid",
  fontSize: "14px",
  gridAutoRows: "max-content",
  minHeight: 0,
  overflow: "auto",

  '& div[data-scope="table"][data-part="body"]': {
    borderColor: "inherit",
    color: "#18181B",
    display: "grid",
    gridColumn: "1 / -1",
    gridTemplateColumns: "subgrid",
  },
  '& div[data-scope="table"][data-part="body-row"]': {
    borderColor: "inherit",
    display: "grid",
    gridColumn: "1 / -1",
    gridTemplateColumns: "subgrid",
    "&:not(:last-child)": { borderBottomWidth: "1px" },
  },
  '& div[data-scope="table"][data-part="body-cell"]': {
    alignItems: "center",
    display: "flex",
    padding: "16px",
  },

  '& div[data-scope="table"][data-part="foot"]': {
    backgroundColor: "inherit",
    borderColor: "inherit",
    bottom: 0,
    color: "#71717A",
    display: "grid",
    fontWeight: 500,
    gridColumn: "1 / -1",
    gridTemplateColumns: "subgrid",
    position: "sticky",
    "&:not(:first-child)": { borderTopWidth: "1px" },
  },
  '&:has([data-scope="table"][data-part="body"]:empty) div[data-scope="table"][data-part="foot"]': {
    borderTopWidth: "0px",
  },
  '& div[data-scope="table"][data-part="foot-row"]': {
    borderColor: "inherit",
    display: "grid",
    gridTemplateColumns: "subgrid",
    gridColumn: "1 / -1",
    "&:not(:first-child)": { borderTopWidth: "1px" },
  },
  '& div[data-scope="table"][data-part="foot-cell"]': {
    padding: "14px",
    display: "flex",
    alignItems: "center",
  },

  '& div[data-scope="table"][data-part="head"]': {
    backgroundColor: "inherit",
    borderColor: "inherit",
    color: "#71717A",
    display: "grid",
    fontWeight: 500,
    gridColumn: "1 / -1",
    gridTemplateColumns: "subgrid",
    position: "sticky",
    top: 0,
    "&:not(:last-child)": { borderBottomWidth: "1px" },
  },
  '&:has([data-scope="table"][data-part="body"]:empty) div[data-scope="table"][data-part="head"]': {
    borderBottomWidth: "0px",
  },
  '& div[data-scope="table"][data-part="head-row"]': {
    borderColor: "inherit",
    display: "grid",
    gridColumn: "1 / -1",
    gridTemplateColumns: "subgrid",
    "&:not(:last-child)": { borderBottomWidth: "1px" },
  },
  '& div[data-scope="table"][data-part="head-cell"]': {
    padding: "14px",
    display: "flex",
    alignItems: "center",
  },
});

export function TableHeadCell({
  children,
  className,
  descendingOrder,
  ...properties
}: Merge<React.ComponentProps<"div">, { descendingOrder: boolean | null }>) {
  return (
    <div
      {...properties}
      className={cx(css({ cursor: "pointer" }), className)}
      data-part="head-cell"
      data-scope="table"
    >
      {children}
      <LuChevronsUpDown
        className={css({
          color: "#8B8B92",
          height: "16px",
          marginLeft: "4px",
          width: "auto",
          '&[data-descending-order="true"] > path:first-child': { color: "#59595E" },
          '&[data-descending-order="false"] > path:last-child': { color: "#59595E" },
        })}
        data-descending-order={descendingOrder}
      />
    </div>
  );
}
